import * as am4charts from '@amcharts/amcharts4/charts';

import { AnomalyType } from '../../../compositions/anomalyTypePicker';
import { ITranslationKeys } from '../../../../../../components/i18n/keys';
import { LowPowerSection } from './lowPowerSection';
import { RebootsSection } from './rebootsSection';
import { DropsSection } from './dropsSection';
import { DataUsageSection } from './dataUsageSection';
import { ProcessHangsSection } from './processHangsSection';
import { ProcessCrashesSection } from './processCrashesSection';

// Props every registered anomaly type's section component receives from the examination page
// shell. Declared here (rather than in its own module) since a dedicated types-only file has no
// executable statements, which some coverage tooling reports as 0% covered.
export interface AnomalySectionProps {
  deviceId: string,
  selectedDate: number,
  /**
   * Registers a chart with the shell's shared axis-width alignment group (see useAlignCharts),
   * so its Y-axis lines up with the top chart. Pass this straight through as a chart's onMount
   * if the section renders exactly one chart that should align with the top chart; sections
   * that render a table (or more than one chart) should ignore it or call it as appropriate.
   */
  onChartMount: (chart: am4charts.XYChart) => am4charts.XYChart
}

export interface AnomalySectionRegistration {
  titleKey: keyof ITranslationKeys['anomalyDetectionBeta'],
  Section: (props: AnomalySectionProps) => JSX.Element,
  /**
   * How many charts this section's own Section component mounts (and registers with the shared
   * useAlignCharts group via onChartMount) - see deviceAnomalyExaminationPage.tsx's own
   * useAlignCharts(1 + chartCount) call. Every current chart-based section renders exactly one;
   * a table-based section (e.g. processHung's ProcessHangsSection) renders none.
   */
  chartCount: number
}

// Maps each anomaly type to the section rendered under the shared examination page's top chart.
// An AnomalyType with no entry is treated as invalid by the route (core-device-anomaly-examination-route.tsx),
// so an unimplemented type degrades gracefully to the not-found redirect rather than a blank page.
export const anomalySections: Partial<Record<AnomalyType, AnomalySectionRegistration>> = {
  deviceLowPower: {
    titleKey: 'LOW_POWER',
    Section: LowPowerSection,
    chartCount: 1
  },
  deviceRebooted: {
    titleKey: 'REBOOTS',
    Section: RebootsSection,
    chartCount: 1
  },
  deviceDropped: {
    titleKey: 'DROPS',
    Section: DropsSection,
    chartCount: 1
  },
  totalDataUsage: {
    titleKey: 'DATA_USAGE',
    Section: DataUsageSection,
    chartCount: 1
  },
  processHung: {
    titleKey: 'PROCESS_HANGS',
    Section: ProcessHangsSection,
    chartCount: 0
  },
  processCrashed: {
    titleKey: 'PROCESS_CRASHES',
    Section: ProcessCrashesSection,
    chartCount: 0
  }
};
